import { FileIcon, UploadIcon, XIcon } from "lucide-react";
import { useRef, useState } from "react";
import { Button, Input } from "web-design-system";

export function FileUpload({
  onChange,
  acceptedTypes = ["application/pdf"],
}: {
  acceptedTypes?: string[];
  onChange: (file: File | null) => void;
}) {
  const [file, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFileTypeAccepted = (fileType: string): boolean => {
    return acceptedTypes.some((acceptedType) => {
      // Handle wildcard patterns like "image/*" or "*/*"
      if (acceptedType.includes("*")) {
        const pattern = acceptedType.replace("*", ".*");
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(fileType);
      }
      // Exact match
      return acceptedType === fileType;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      if (isFileTypeAccepted(droppedFile.type)) {
        setUploadedFile(droppedFile);
        onChange(droppedFile);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedFile(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div
      className={`rounded-lg relative border gap-2 bg-background border-border border-dashed flex flex-col flex-1 justify-center items-center transition-colors ${
        file
          ? "cursor-default"
          : `cursor-pointer ${
              isDragging
                ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                : "hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/50"
            }`
      }`}
      onClick={file ? undefined : handleClick}
      onDragOver={file ? undefined : handleDragOver}
      onDragLeave={file ? undefined : handleDragLeave}
      onDrop={file ? undefined : handleDrop}
    >
      {file ? (
        <>
          <div className="flex items-center max-w-[80%] p-2 gap-3 rounded-lg bg-secondary">
            <FileIcon className="size-5 stroke-1" />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {(file.size / 1024).toFixed(2)} KB
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={handleRemoveFile}
            >
              <XIcon className="size-4" />
            </Button>
          </div>
        </>
      ) : (
        <>
          <UploadIcon className="size-8 stroke-1" />
          <span className="text-sm text-center">
            Drag and drop file here or <br />
            <span className="text-blue-500">Upload from computer</span>
          </span>
          <Input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setUploadedFile(file);
              onChange(file);
            }}
            accept={acceptedTypes.join(",")}
          />
        </>
      )}
    </div>
  );
}
